/**
 * src/utils/ui/device.ts - Device & Camera Utilities
 * =====================================================================
 * Utility: Comprehensive Device Detection and Camera Management
 * 
 * Description
 * -----------
 * Provides comprehensive device detection, browser identification, and 
 * camera constraint utilities. Includes OS and browser-specific camera 
 * constraints to ensure compatibility across different platforms. 
 * macOS/Safari require simplified constraints to prevent "Face detection 
 * searching" hangs and getUserMedia failures.
 * 
 * Author: Uttam Panda
 * Version: 3.0.0 (Comprehensive Device & Camera Utilities)
 * Created: 2026-02-09
 * Last Updated: 2026-02-09
 * 
 * Key Functions
 * -------------
 * 1. Device Detection: OS, browser, mobile detection
 * 2. Camera Management: Camera info, available cameras
 * 3. Camera Constraints: Platform-optimized constraints
 * 4. Constraint Validation: Compatibility checking
 * 5. Camera Testing: Test constraints before use
 * 
 * Platform Support Matrix
 * -----------------------
 * | Platform  | Optimal Constraints        | Fallback              |
 * |-----------|----------------------------|-----------------------|
 * | Chrome    | Full HD, 60fps             | HD, 30fps             |
 * | Firefox   | HD, 30fps                  | VGA, 15fps            |
 * | Safari    | Simplified (no frameRate)  | { video: true }       |
 * | macOS     | Width/height only          | { video: true }       |
 * | iOS       | Mobile-optimized           | { video: true }       |
 * =====================================================================
 */

import { devLog, devWarn } from '../core/devLog';
import { logger } from '../core/dystestLogger';

// Add global interface extension for MSStream
declare global {
  interface Window {
    MSStream?: unknown;
  }
}

// ============================================================================
// DEVICE INFORMATION INTERFACES
// ============================================================================

export interface DeviceInfo {
  browser: string;
  os: string;
  isMobile: boolean;
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
  userAgent: string;
}

export interface CameraInfo {
  label: string;
  deviceId: string;
  resolution: string;
  facingMode?: string | undefined;
  frameRate?: number | undefined;
  groupId?: string | undefined;
}

// ============================================================================
// DEVICE DETECTION FUNCTIONS
// ============================================================================

/**
 * Get comprehensive device information
 * 
 * @returns DeviceInfo object with all device details
 */
export const getDeviceInfo = (): DeviceInfo => {
  // Safe check for server-side rendering
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      browser: 'unknown',
      os: 'unknown',
      isMobile: false,
      screenResolution: 'unknown',
      timezone: 'unknown',
      language: 'unknown',
      platform: 'unknown',
      userAgent: 'unknown',
    };
  }

  const ua = navigator.userAgent;
  const isMobile = /Mobi|Android/i.test(ua);
  const os = getOSName();
  const browser = getBrowserName();
  const platform = navigator.platform;

  return {
    browser,
    os,
    isMobile,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform,
    userAgent: ua.substring(0, 150), // Limit length
  };
};

/**
 * Get browser name from user agent
 * 
 * @returns Browser name (Chrome, Firefox, Safari, Edge, Opera, or Unknown)
 */
export const getBrowserName = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
  return 'Unknown';
};

/**
 * Get operating system name
 * 
 * @returns OS name (Windows, macOS, Linux, Android, iOS, or Unknown)
 */
export const getOSName = (): string => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  // More accurate detection
  if (/Android/.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS';
  if (/Win/.test(platform)) return 'Windows';
  if (/Mac/.test(platform)) return 'macOS';
  if (/Linux/.test(platform)) return 'Linux';
  if (/CrOS/.test(userAgent)) return 'Chrome OS';
  
  return 'Unknown';
};

export type DeviceClass = 'mobile' | 'tablet' | 'desktop';

/**
 * Phone, tablet or computer, for recording where the registration form was sent from
 * (phones and tablets are stopped at camera setup, see MobileGate). Same detection as
 * isMobileDevice: everything isMobileDevice blocks is 'mobile' or 'tablet'.
 */
export const getDeviceClass = (): DeviceClass => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
  if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1) return 'tablet'; // iPadOS reports a Mac
  if (/Mobi|Android|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return 'mobile';
  return 'desktop';
};

/** Short "OS · browser" label (no full user-agent string is stored). */
export const getDeviceSummary = (): string => `${getOSName()} · ${getBrowserName()}`.slice(0, 80);

/**
 * Check if device is mobile
 * 
 * @returns True if mobile device
 */
export const isMobileDevice = (): boolean => {
  const ua = navigator.userAgent;
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;
  // iPadOS 13+ Safari reports a desktop Mac user agent. A "Mac" with a multi-touch
  // screen is an iPad (real Macs report 0 touch points), usually held in the hand.
  return /Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
};

/**
 * Check if running on macOS
 * 
 * @returns True if macOS
 */
export const isMacOS = (): boolean => {
  return navigator.platform.includes('Mac') || navigator.userAgent.includes('Mac');
};

/**
 * Check if using Safari browser
 * 
 * @returns True if Safari browser
 */
export const isSafariBrowser = (): boolean => {
  const userAgent = navigator.userAgent;
  return /^((?!chrome|android).)*safari/i.test(userAgent);
};

/**
 * Check if running on iOS
 * 
 * @returns True if iOS device
 */
export const isiOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

/**
 * Check if using Chrome browser
 * 
 * @returns True if Chrome browser
 */
export const isChrome = (): boolean => {
  return navigator.userAgent.includes('Chrome') && !navigator.userAgent.includes('Edg');
};

/**
 * Check if using Firefox browser
 * 
 * @returns True if Firefox browser
 */
export const isFirefox = (): boolean => {
  return navigator.userAgent.includes('Firefox');
};

/**
 * Check if using Edge browser
 * 
 * @returns True if Edge browser
 */
export const isEdge = (): boolean => {
  return navigator.userAgent.includes('Edg');
};

// ============================================================================
// CAMERA MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Gets camera information from a MediaStream
 * 
 * @param stream - MediaStream from getUserMedia
 * @returns CameraInfo or null if unavailable
 */
export const getCameraInfo = async (stream: MediaStream): Promise<CameraInfo | null> => {
  try {
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return null;

    const settings = videoTrack.getSettings();
    const label = videoTrack.label || 'Unknown Camera';

    const info: CameraInfo = {
      label,
      deviceId: settings.deviceId || 'unknown',
      resolution: `${settings.width || 0}x${settings.height || 0}`,
      frameRate: settings.frameRate ?? undefined,
      groupId: videoTrack.getSettings().groupId ?? undefined,
    };

    // Only add facingMode if it exists
    if (settings.facingMode) {
      info.facingMode = settings.facingMode;
    }

    return info;
  } catch (error) {
    logger.error('Failed to get camera info:', { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
};

/**
 * Gets all available camera devices
 * 
 * @returns Array of available camera devices
 */
export const getAvailableCameras = async (): Promise<MediaDeviceInfo[]> => {
  try {
    // Ensure permissions are granted first
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      devWarn('MediaDevices API not supported');
      return [];
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === 'videoinput');
  } catch (error) {
    logger.error('Failed to enumerate cameras:', { error: error instanceof Error ? error.message : String(error) });
    return [];
  }
};

/**
 * Get the default front-facing camera constraints
 * 
 * @returns Default front camera constraints
 */
export const getDefaultFrontCameraConstraints = (): MediaStreamConstraints => {
  return {
    video: {
      facingMode: 'user',
      width: { ideal: 1280 },
      height: { ideal: 720 },
    },
    audio: false,
  };
};

// ============================================================================
// CAMERA CONSTRAINT UTILITIES
// ============================================================================

/**
 * Get optimal camera constraints for current platform/browser
 * 
 * @param width - Desired width (default: 1280)
 * @param height - Desired height (default: 720)
 * @param facingMode - Camera facing mode (default: 'user')
 * @returns MediaStreamConstraints optimized for current platform
 */
export const getOptimalCameraConstraints = (
  width: number = 1280,
  height: number = 720,
  facingMode: 'user' | 'environment' = 'user'
): MediaStreamConstraints => {
  const isMac = isMacOS();
  const isSafari = isSafariBrowser();
  const isIOS = isiOS();
  const browser = getBrowserName();
  const os = getOSName();
  
  devLog('📷 Platform Detection:', {
    os,
    browser,
    isMac,
    isSafari,
    isIOS,
    userAgent: navigator.userAgent.substring(0, 80) + '...'
  });

  // iOS devices (iPhone/iPad) - Most restrictive
  if (isIOS) {
    devLog('📱 Using iOS-optimized camera constraints');
    return {
      video: {
        width: { ideal: Math.min(width, 1920) },
        height: { ideal: Math.min(height, 1080) },
        facingMode,
        // iOS Safari has strict constraints
        ...(isSafari ? {} : { frameRate: { ideal: 30 } })
      },
      audio: false
    };
  }

  // macOS Safari (very restrictive)
  if (isMac && isSafari) {
    devLog('🦁 Using macOS Safari-optimized camera constraints');
    return {
      video: {
        width: { ideal: Math.min(width, 1280) },
        height: { ideal: Math.min(height, 720) },
        facingMode,
        // Safari doesn't support advanced constraints well
        // Avoid frameRate specification for Safari
      },
      audio: false
    };
  }

  // macOS other browsers (Chrome/Firefox on Mac)
  if (isMac) {
    devLog('🍎 Using macOS-optimized camera constraints');
    return {
      video: {
        width: { ideal: Math.min(width, 1280) },
        height: { ideal: Math.min(height, 720) },
        facingMode,
        frameRate: { ideal: 30, max: 60 }
      },
      audio: false
    };
  }

  // Windows/Linux Chrome (optimal settings)
  if (browser === 'Chrome') {
    devLog('🟢 Using Chrome-optimized camera constraints');
    return {
      video: {
        width: { ideal: width, max: 1920 },
        height: { ideal: height, max: 1080 },
        facingMode,
        frameRate: { ideal: 60, min: 30 },
        // Chrome supports advanced features
        ...(navigator.userAgent.includes('Chrome/') && 
            parseInt(navigator.userAgent.match(/Chrome\/(\d+)/)?.[1] || '0') >= 59 ? {
          resizeMode: 'crop-and-scale',
        } : {}),
        noiseSuppression: true,
        echoCancellation: false
      } as MediaTrackConstraints,
      audio: false
    };
  }

  // Windows/Linux Firefox
  if (browser === 'Firefox') {
    devLog('🦊 Using Firefox-optimized camera constraints');
    return {
      video: {
        width: { ideal: Math.min(width, 1280) },
        height: { ideal: Math.min(height, 720) },
        facingMode,
        frameRate: { ideal: 30 }
      },
      audio: false
    };
  }

  // Windows/Linux Edge
  if (browser === 'Edge') {
    devLog('🔷 Using Edge-optimized camera constraints');
    return {
      video: {
        width: { ideal: Math.min(width, 1920) },
        height: { ideal: Math.min(height, 1080) },
        facingMode,
        frameRate: { ideal: 30 }
      },
      audio: false
    };
  }

  // Default/fallback for other browsers
  devLog('🌐 Using default camera constraints');
  return {
    video: {
      width: { ideal: Math.min(width, 1280) },
      height: { ideal: Math.min(height, 720) },
      facingMode,
      frameRate: { ideal: 30 }
    },
    audio: false
  };
};

/**
 * Get fallback camera constraints for when optimal constraints fail
 * Uses minimal constraints that should work on any platform
 * 
 * @returns Minimal MediaStreamConstraints
 */
export const getFallbackConstraints = (): MediaStreamConstraints => {
  devLog('🔄 Using fallback camera constraints (minimal)');
  
  if (isSafariBrowser() || isMacOS()) {
    // Safari/macOS: absolute minimum
    return { video: true, audio: false };
  }
  
  // Other browsers: slightly better fallback
  return {
    video: {
      width: { ideal: 640 },
      height: { ideal: 480 },
      facingMode: 'user'
    },
    audio: false
  };
};

/**
 * Get progressive camera constraints (tries optimal, falls back if fails)
 * 
 * @param width - Desired width
 * @param height - Desired height
 * @returns Array of constraints to try in order
 */
export const getProgressiveConstraints = (
  width: number = 1280,
  height: number = 720
): MediaStreamConstraints[] => {
  return [
    getOptimalCameraConstraints(width, height),
    {
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: 'user',
        frameRate: { ideal: 30 }
      },
      audio: false
    },
    {
      video: {
        width: { ideal: 640 },
        height: { ideal: 480 },
        facingMode: 'user'
      },
      audio: false
    },
    getFallbackConstraints()
  ];
};

/**
 * Validate if constraints are likely to work on current platform
 * 
 * @param constraints - Constraints to validate
 * @returns Object with validation result and suggestions
 */
export const validateConstraints = (constraints: MediaStreamConstraints): {
  isValid: boolean;
  issues: string[];
  suggestions: string[];
  platform: string;
  browser: string;
} => {
  const issues: string[] = [];
  const suggestions: string[] = [];
  
  // ConstrainDouble/ConstrainULong are union types (number | {min?, max?, ideal?});
  // accessing .ideal on them fails unless we cast to the ConstraintRange shape
  // we actually use here. A local interface avoids the any cast.
  type VideoExt = { frameRate?: { ideal?: number }; width?: { ideal?: number }; height?: { ideal?: number }; resizeMode?: string };
  const videoConstraints = constraints.video as VideoExt | undefined;
  const platform = getOSName();
  const browser = getBrowserName();
  
  // Check for Safari/macOS incompatible settings
  if (isSafariBrowser() || isMacOS()) {
    if ((videoConstraints?.frameRate?.ideal ?? 0) > 30) {
      issues.push('Safari/macOS may not support high frame rates');
      suggestions.push('Use frameRate: { ideal: 30 } or omit frameRate');
    }
    
    if ((videoConstraints?.width?.ideal ?? 0) > 1280 || (videoConstraints?.height?.ideal ?? 0) > 720) {
      issues.push('Safari/macOS may struggle with high resolutions');
      suggestions.push('Use max 1280x720 resolution');
    }
    
    if (videoConstraints?.resizeMode) {
      issues.push('Safari does not support resizeMode constraint');
      suggestions.push('Remove resizeMode from constraints');
    }
  }
  
  // Check for iOS restrictions
  if (isiOS()) {
    if ((videoConstraints?.frameRate?.ideal ?? 0) > 30) {
      issues.push('iOS devices may limit frame rates');
      suggestions.push('Use frameRate: { ideal: 30 }');
    }
    
    if ((videoConstraints?.width?.ideal ?? 0) > 1920 || (videoConstraints?.height?.ideal ?? 0) > 1080) {
      issues.push('iOS may not support resolutions above 1920x1080');
      suggestions.push('Use max 1920x1080 resolution');
    }
  }
  
  // Check for Firefox limitations
  if (isFirefox()) {
    if ((videoConstraints?.frameRate?.ideal ?? 0) > 60) {
      issues.push('Firefox may not support frame rates above 60fps');
      suggestions.push('Use max frameRate: { ideal: 60 }');
    }
  }
  
  return {
    isValid: issues.length === 0,
    issues,
    suggestions,
    platform,
    browser
  };
};

/**
 * Test camera constraints to see if they work
 * 
 * @param constraints - Constraints to test
 * @param timeoutMs - Timeout in milliseconds (default: 5000)
 * @returns Promise resolving to test result
 */
export const testCameraConstraints = async (
  constraints: MediaStreamConstraints,
  timeoutMs: number = 5000
): Promise<{
  success: boolean;
  error?: string | undefined;
  stream?: MediaStream;
  duration: number;
  cameraInfo?: CameraInfo | null;
}> => {
  const startTime = Date.now();
  
  try {
    // Use Promise.race for timeout instead of AbortController signal
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Timeout')), timeoutMs);
    });
    
    const getUserMediaPromise = navigator.mediaDevices.getUserMedia(constraints);
    
    const stream = await Promise.race([getUserMediaPromise, timeoutPromise]);
    
    const duration = Date.now() - startTime;
    
    // Get camera info from the stream
    const cameraInfo = await getCameraInfo(stream);
    
    // Clean up test stream
    stream.getTracks().forEach(track => track.stop());
    
    return {
      success: true,
      stream,
      duration,
      cameraInfo
    };
  } catch (error: unknown) {
    const duration = Date.now() - startTime;

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      duration
    };
  }
};

/**
 * Get camera capabilities for a specific device
 * 
 * @param deviceId - Camera device ID
 * @returns Promise with camera capabilities
 */
export const getCameraCapabilities = async (deviceId?: string): Promise<{
  supported: boolean;
  capabilities?: MediaTrackCapabilities | undefined;
  constraints?: MediaTrackConstraints | undefined;
}> => {
  try {
    // Create a test stream to get capabilities
    const testConstraints: MediaStreamConstraints = {
      video: deviceId ? { deviceId: { exact: deviceId } } : true,
      audio: false
    };
    
    const stream = await navigator.mediaDevices.getUserMedia(testConstraints);
    const videoTrack = stream.getVideoTracks()[0];
    
    if (!videoTrack) {
      stream.getTracks().forEach(track => track.stop());
      return { supported: false };
    }
    
    const capabilities = videoTrack.getCapabilities ? videoTrack.getCapabilities() : undefined;
    const constraints = videoTrack.getConstraints ? videoTrack.getConstraints() : undefined;
    
    // Clean up
    stream.getTracks().forEach(track => track.stop());
    
    return {
      supported: true,
      capabilities,
      constraints
    };
  } catch (error) {
    logger.error('Failed to get camera capabilities:', { error: error instanceof Error ? error.message : String(error) });
    return { supported: false };
  }
};

// ============================================================================
// EXPORT ALL UTILITIES
// ============================================================================

const deviceUtils = {
  // Device Detection
  getDeviceInfo,
  getBrowserName,
  getOSName,
  isMobileDevice,
  isMacOS,
  isSafariBrowser,
  isiOS,
  isChrome,
  isFirefox,
  isEdge,
  
  // Camera Management
  getCameraInfo,
  getAvailableCameras,
  getDefaultFrontCameraConstraints,
  
  // Camera Constraints
  getOptimalCameraConstraints,
  getFallbackConstraints,
  getProgressiveConstraints,
  validateConstraints,
  testCameraConstraints,
  getCameraCapabilities,
};

export default deviceUtils;